import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, PenLine, Archive, Lock, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, StatusBadge } from "@/components/admin/common";
import { CardsTable } from "@/components/admin/CardsTable";
import { ClientFormDialog } from "@/components/admin/ClientFormDialog";
import { NewCardDialog } from "@/components/admin/NewCardDialog";
import { ClientAccessPanel } from "@/components/admin/ClientAccessPanel";
import { NewQrDialog, QrLinkList } from "@/pages/QrLinks";
import { ReportPanel } from "@/components/admin/ReportPanel";
import { api, formatErr } from "@/lib/api";

const Info = ({ label, value }) => (
  <div><div className="eyebrow">{label}</div><div className="mt-1 text-sm text-zinc-800 whitespace-pre-line">{value || "—"}</div></div>
);

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [cards, setCards] = useState([]);
  const [edit, setEdit] = useState(false);
  const [newCard, setNewCard] = useState(false);
  const [qrs, setQrs] = useState([]);
  const [newQr, setNewQr] = useState(false);
  const load = () => {
    api.get("/qr-links", { params: { client_id: id } }).then((r) => setQrs(r.data));
    api.get(`/clients/${id}`).then((r) => setClient(r.data));
    api.get("/cards", { params: { client_id: id, status: "all" } }).then((r) => setCards(r.data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!client) return <div className="eyebrow">Caricamento…</div>;

  const archive = async () => {
    try {
      await api.put(`/clients/${id}`, { ...client, status: client.status === "archived" ? "active" : "archived" });
      toast.success(client.status === "archived" ? "Cliente ripristinato" : "Cliente archiviato");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const delQr = async (it) => {
    if (!window.confirm(`Eliminare "${it.name}"? Il QR stampato smetterà di funzionare.`)) return;
    await api.delete(`/qr-links/${it.id}`);
    toast.success("QR eliminato");
    load();
  };

  return (
    <div>
      <Link to="/admin/clienti" className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" />Clienti</Link>
      <PageHeader eyebrow={client.company || "Cliente"} title={client.name}>
        <Button variant="outline" onClick={() => setEdit(true)} data-testid="client-detail-edit"><PenLine className="mr-1.5 h-4 w-4" />Modifica</Button>
        <Button variant="outline" onClick={archive} data-testid="client-detail-archive"><Archive className="mr-1.5 h-4 w-4" />{client.status === "archived" ? "Ripristina" : "Archivia"}</Button>
        <Button onClick={() => setNewCard(true)} data-testid="client-detail-new-card"><Plus className="mr-1.5 h-4 w-4" />Nuova card</Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
        <div className="space-y-6">
        <aside className="panel h-fit space-y-5 p-6" data-testid="client-private-info">
          <div className="flex items-center justify-between"><StatusBadge status={client.status} testId="client-detail-status" /><span className="flex items-center gap-1 text-[11px] text-zinc-400"><Lock className="h-3 w-3" />Dati privati</span></div>
          <Info label="Email" value={client.email} />
          <Info label="Telefono" value={client.phone} />
          <Info label="P. IVA / C.F." value={client.vat_number} />
          <Info label="Note interne" value={client.notes} />
        </aside>
        <ClientAccessPanel client={client} />
        <ReportPanel clientId={id} />
        </div>
        <div className="min-w-0">
          <div className="mb-3 eyebrow">Card del cliente · {cards.length}</div>
          <CardsTable cards={cards} onChanged={load} showClient={false} />
          <div className="mb-3 mt-10 flex items-center justify-between">
            <div className="eyebrow" data-testid="client-qr-count">QR da link · {qrs.length}</div>
            <Button variant="outline" size="sm" onClick={() => setNewQr(true)} data-testid="client-detail-new-qr"><QrCode className="mr-1.5 h-4 w-4" />Nuovo QR</Button>
          </div>
          {qrs.length ? <QrLinkList items={qrs} onDelete={delQr} testId="client-qr-list" /> : (
            <div className="panel px-5 py-8 text-center text-sm text-zinc-500" data-testid="client-qr-empty">Nessun QR da link per questo cliente.</div>
          )}
        </div>
      </div>
      <ClientFormDialog open={edit} onOpenChange={setEdit} client={client} onSaved={load} />
      <NewCardDialog open={newCard} onOpenChange={setNewCard} clientId={id} />
      <NewQrDialog open={newQr} onOpenChange={setNewQr} clientId={id} />
    </div>
  );
}
